import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { GolfEvent } from '../../../models/GolfEvent';
import { GolfEventService } from '../../../services/golf-event.service';
import { ApplicationService } from '../../../services/application.service';
import { LeagueLeaderboard } from '../../../models/leagueleaderboard';
import { LeagueWeek } from '../../../models/LeagueWeek';
import { LeagueRound } from '../../../models/LeagueRound';
import { EVENT_FORMAT } from '../../../models/constants';

interface BreakdownRound {
  dateLabel:string;
  partnerLabel:string;
  grossScore:number;
  netScore:number;
  counted:boolean;
}

interface CourseBreakdown {
  courseName:string;
  coursePar:number;
  roundsToCount:number;
  postedCount:number;
  isOfficial:boolean;
  rounds:Array<BreakdownRound>;
  countedNets:Array<number>;
  roundedAvg:number|null;
  scoreToPar:number|null;
}

interface ScoreBreakdown {
  playerName:string;
  courses:Array<CourseBreakdown>;
  eventTotalToPar:number|null;
  isOfficial:boolean;
}

@Component({
  selector: 'app-league-leaderboard',
  standalone: true,
  imports: [NgFor, NgIf],
  templateUrl: './league-leaderboard.component.html',
  styleUrl: './league-leaderboard.component.css'
})
export class LeagueLeaderboardComponent implements OnChanges {

  constructor(private applicationService:ApplicationService, private golfEventService:GolfEventService) {}
  
  @Input() league:GolfEvent|null = null;
  isTeamEvent:boolean = false;
  leaderboard:Array<LeagueLeaderboard> = [];
  breakdown:ScoreBreakdown|null = null;
  loadingBreakdown:boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['league'] && changes['league'].currentValue) {
      if(this.league) {
        // Hybrid scramble is played in pairs but ranked individually.
        this.isTeamEvent = this.league.isTeamEvent && this.league.type !== EVENT_FORMAT.HYBRID_SCRAMBLE;
      }
      this.getLeaderboard();
    }
  }

  get isHybridScramble():boolean {
    return this.league?.type === EVENT_FORMAT.HYBRID_SCRAMBLE;
  }

  async getLeaderboard() {
    if(!this.league) return;
    this.applicationService.showGlobalSpinner();
    this.leaderboard = await this.golfEventService.getLeagueLeaderboard(this.league.id);
    this.applicationService.hideGlobalSpinner();
  }

  hasScore(entry:LeagueLeaderboard):boolean {
    if (entry.hasScore === false) return false;
    if (entry.hasScore === true) return true;
    return entry.totalScoreToPar !== null && entry.totalScoreToPar !== undefined;
  }

  isOfficialScore(entry:LeagueLeaderboard):boolean {
    return this.hasScore(entry) && (!this.isHybridScramble || entry.isOfficial === true);
  }

  getRank(index:number):number {
    let rank = 0;
    for (let i = 0; i <= index; i++) {
      if (this.isOfficialScore(this.leaderboard[i])) {
        rank++;
      }
    }
    return rank;
  }

  formatScore(entry:LeagueLeaderboard):string {
    if (!this.hasScore(entry)) {
      return 'NS';
    }
    return this.formatScoreToPar(Number(entry.totalScoreToPar));
  }

  formatScoreToPar(score:number):string {
    if(!this.league)
      return score.toString();

    if(this.league.scoringType === 'SCORETOPAR') {
      return this.applicationService.formatScoreToPar(Number(score));
    }
    else
      return Number(score).toString();
  }

  playerLabel(entry:LeagueLeaderboard):string {
    if (this.isTeamEvent) {
      return entry.teamName ?? '';
    }
    return `${entry.individualFirstName ?? ''} ${entry.individualLastName ?? ''}`.trim();
  }

  canShowBreakdown(entry:LeagueLeaderboard):boolean {
    return this.isHybridScramble && this.hasScore(entry) && entry.playerId != null;
  }

  courseProgress(entry:LeagueLeaderboard):string {
    const done = entry.officialCourseCount ?? 0;
    const total = entry.courseCount ?? 0;
    return `${done} of ${total} courses completed`;
  }

  async openBreakdown(entry:LeagueLeaderboard, event:Event):Promise<void> {
    event.stopPropagation();
    if (!this.canShowBreakdown(entry) || !this.league || entry.playerId == null) return;

    this.loadingBreakdown = true;
    this.breakdown = null;
    this.applicationService.showGlobalSpinner();

    try {
      const weeks = await this.golfEventService.getLeagueWeeks(this.league.id);
      const courses:Array<CourseBreakdown> = [];

      for (const week of weeks) {
        const allRounds = await this.golfEventService.getLeagueRounds(week.id);
        const playerRounds = allRounds
          .filter(r => r.playerId != null && r.playerId.toString() === entry.playerId!.toString())
          .sort((a, b) => a.score - b.score);

        if (playerRounds.length === 0 && !this.isHybridScramble) continue;

        courses.push(this.buildCourseBreakdown(week, playerRounds));
      }

      const eventTotal = courses.reduce((sum, course) => {
        return sum + (course.scoreToPar ?? 0);
      }, 0);
      const scoredCourses = courses.filter(course => course.scoreToPar !== null);

      this.breakdown = {
        playerName: this.playerLabel(entry),
        courses,
        eventTotalToPar: scoredCourses.length > 0 ? eventTotal : null,
        isOfficial: courses.length > 0 && courses.every(course => course.isOfficial)
      };
    } finally {
      this.loadingBreakdown = false;
      this.applicationService.hideGlobalSpinner();
    }
  }

  closeBreakdown():void {
    this.breakdown = null;
  }

  buildCourseBreakdown(week:LeagueWeek, playerRounds:Array<LeagueRound>):CourseBreakdown {
    const roundsToCount = week.roundsToCount || 1;
    const postedCount = playerRounds.length;
    const isOfficial = postedCount >= roundsToCount;
    const best = playerRounds.slice(0, roundsToCount);
    const bestIds = new Set(best.map(r => r.id));
    const countedNets = best.map(r => r.score);

    let roundedAvg:number|null = null;
    let scoreToPar:number|null = null;
    if (countedNets.length > 0) {
      const total = countedNets.reduce((sum, net) => sum + net, 0);
      roundedAvg = Math.round(total / countedNets.length);
      scoreToPar = roundedAvg - week.coursePar;
    }

    // Show newest first in the list for readability, but "counted" follows best-N by net.
    const displayRounds = [...playerRounds].sort((a, b) => {
      return new Date(b.createdDateUTC).getTime() - new Date(a.createdDateUTC).getTime();
    });

    return {
      courseName: week.golfCourseName,
      coursePar: week.coursePar,
      roundsToCount,
      postedCount,
      isOfficial,
      countedNets,
      roundedAvg,
      scoreToPar,
      rounds: displayRounds.map(r => ({
        dateLabel: this.applicationService.formatLocalDateTime(r.createdDateUTC),
        partnerLabel: r.guestPartnerName
          ? `${r.guestPartnerName} (Guest)`
          : r.partnerFirstName
            ? [r.partnerFirstName, r.partnerLastName].filter(Boolean).join(' ')
            : '—',
        grossScore: r.grossScore,
        netScore: r.score,
        counted: bestIds.has(r.id)
      }))
    };
  }

  countedNetsLabel(course:CourseBreakdown):string {
    if (course.countedNets.length === 0) return '—';
    return course.countedNets.join(', ');
  }
}
